import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import './OrderStatusPage.css'

const GET_ORDER = gql`
  query GetOrder($orderId: String!) {
    getOrder(orderId: $orderId) {
      id
      orderId
      userId
      amount
      phone
      operatorId
      planId
      status
      paymentId
      createdAt
    }
  }
`

const GET_PAYMENT = gql`
  query GetPaymentStatus($transactionId: String!) {
    getPaymentStatus(transactionId: $transactionId) {
      id
      txnId
      userId
      amount
      status
      planId
      paymentMethod
      createdAt
      completedAt
    }
  }
`

function OrderStatusPage() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [paymentStatus, setPaymentStatus] = useState(null)

  const { data: orderData, loading: orderLoading, refetch } = useQuery(GET_ORDER, {
    variables: { orderId },
    onError: () => navigate('/', { state: { from: location } }),
    pollInterval: 3000 // Poll every 3 seconds
  })

  useEffect(() => {
    // Check URL params for payment status
    const params = new URLSearchParams(window.location.search)
    const status = params.get('status')
    const txnid = params.get('txnid')

    if (status && txnid) {
      setPaymentStatus(status)
    }
  }, [])

  if (orderLoading) {
    return (
      <div className="order-status-page">
        <div className="container">
          <div className="loading"><div className="spinner"></div></div>
        </div>
      </div>
    )
  }

  const order = orderData?.getOrder
  if (!order) {
    return (
      <div className="order-status-page">
        <div className="container">
          <div className="status-container">
            <h2>Order Not Found</h2>
            <button className="btn-primary" onClick={() => navigate('/', { state: { from: location } })}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✓'
      case 'failed':
        return '✗'
      case 'pending':
        return '⏳'
      default:
        return '?'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success'
      case 'failed':
        return 'failed'
      case 'pending':
        return 'pending'
      default:
        return 'pending'
    }
  }

  return (
    <div className="order-status-page">
      <div className="container">
        <div className="status-container">
          <div className={`status-badge ${getStatusColor(order.status)}`}>
            <span className="icon">{getStatusIcon(order.status)}</span>
            <span className="status-text">{order.status.toUpperCase()}</span>
          </div>

          <h1>
            {order.status === 'completed' && 'Recharge Successful!'}
            {order.status === 'pending' && 'Processing Your Recharge...'}
            {order.status === 'failed' && 'Recharge Failed'}
          </h1>

          {order.status === 'completed' && (
            <p className="success-message">
              Your mobile recharge of ₹{order.amount} has been successfully processed!
            </p>
          )}

          {order.status === 'pending' && (
            <p className="pending-message">
              Your recharge request is being processed. Please wait...
            </p>
          )}

          {order.status === 'failed' && (
            <p className="failed-message">
              Unfortunately, your recharge request failed. Please try again.
            </p>
          )}

          <div className="order-details">
            <h3>Order Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Order ID:</span>
                <span className="detail-value">{order.orderId}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone Number:</span>
                <span className="detail-value">{order.phone}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">₹{order.amount}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status ${order.status}`}>
                  {order.status.toUpperCase()}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date & Time:</span>
                <span className="detail-value">
                  {new Date(order.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            {order.status === 'failed' && (
              <button className="btn-primary" onClick={() => navigate('/recharge')}>
                Try Again
              </button>
            )}
            {order.status === 'pending' && (
              <button className="btn-secondary" onClick={() => refetch()}>
                Refresh Status
              </button>
            )}
            <button className="btn-secondary" onClick={() => navigate('/', { state: { from: location } }) }>
              Go Home
            </button>
          </div>

          {order.status === 'completed' && (
            <div className="next-steps">
              <h4>Next Steps:</h4>
              <ul>
                <li>Your balance will be credited within 2-5 minutes</li>
                <li>Check your mobile operator app or call your operator's USSD code</li>
                <li>Keep your order ID ({order.orderId}) for reference</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderStatusPage
